import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'
import AccountCreatePanel from '../components/AccountCreatePanel'
import AccountTable from '../components/AccountTable'
import {
  bankTypeLookup,
  bankTypeOptions,
  normalizeBankTypeValue,
} from '../constants/bankTypes'
import bankAccountService from '../../../services/bankAccountService'
import ConfirmationDialog from '../../../components/ConfirmationDialog'
import { MdDataSaverOff } from 'react-icons/md'
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

const MobileAccountList = ({ accounts = [], onEdit, onDelete, bankLookup = new Map() }) => {
  const [search, setSearch] = useState('')

  const filteredAccounts = useMemo(() => {
    if (!search.trim()) return accounts

    const keyword = search.trim().toLowerCase()
    return accounts.filter((account) => {
      const bank = bankLookup.get(account.bankType) ?? {}
      return (
        account.accountName?.toLowerCase().includes(keyword) ||
        account.accountNumber?.toLowerCase().includes(keyword) ||
        bank.name?.toLowerCase().includes(keyword)
      )
    })
  }, [accounts, search, bankLookup])

  return (
    <section className="rounded-3xl border border-customSurface/30 bg-white p-5 shadow-lg shadow-slate-900/5 dark:border-customSurface dark:bg-customSurface">
      <header className="flex flex-col gap-4">
        <div>
          <h3 className="text-xl font-semibold text-customInk dark:text-customHeading">
            Saved payment accounts
          </h3>
          <p className="text-sm text-customSecondaryText dark:text-customMuted">
            Search, edit, or remove existing payment accounts on the go.
          </p>
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search account or bank"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-3 text-sm text-customPrimaryText focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/30 dark:border-white/10 dark:bg-customSurfaceMuted dark:text-customHeading"
          />
        </div>
      </header>

      <div className="mt-5 grid gap-4">
        {filteredAccounts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 px-6 py-10 text-center text-sm text-slate-500 dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60 dark:text-customMuted">
            No accounts matched your search. Try a different keyword or create a new account.
          </div>
        ) : (
          filteredAccounts.map((account) => {
            const bank = bankLookup.get(account.bankType) ?? account.bankType
            return (
              <article
                key={account.id ?? account.accountNumber}
                className="rounded-2xl border border-customSurface/30 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-customMuted/30 dark:bg-customSurfaceMuted"
              >
                <div className="flex items-start gap-3">
                  {bank?.icon && (
                    <img
                      src={bank.icon}
                      alt={bank?.name ?? 'Bank'}
                      className="h-10 w-10 shrink-0 rounded-full border border-white/70 object-contain shadow-sm"
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-customMuted dark:text-customSecondaryText">
                      Bank
                    </p>
                    <p className="truncate text-base font-semibold text-customPrimaryText dark:text-customHeading">
                      {bank?.name ?? 'Unknown bank'}
                    </p>
                    {bank?.description && (
                      <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">
                        {bank.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid gap-2 text-sm text-customPrimaryText dark:text-customHeading">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-customMuted dark:text-customSecondaryText">
                      Account name
                    </p>
                    <p className="font-semibold">{account.accountName}</p>
                  </div>
                  {account.accountNumber && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-customMuted dark:text-customSecondaryText">
                        Account number
                      </p>
                      <p className="font-mono text-sm">{account.accountNumber}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between rounded-xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
                    <span>Created on</span>
                    <span>{account.createdDate}</span>
                  </div>
                </div>

                <footer className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit?.(account)}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-xl bg-customAccent/10 px-3 py-2 text-xs font-semibold text-customAccent transition hover:bg-customAccent/20 sm:flex-none sm:px-4"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete?.(account)}
                    className="inline-flex flex-1 items-center justify-center gap-1 rounded-xl bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-500 transition hover:bg-rose-500/20 sm:flex-none sm:px-4"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </button>
                </footer>
              </article>
            )
          })
        )}
      </div>
    </section>
  )
}

const initialForm = {
  accountName: '',
  accountNumber: '',
  bankType: bankTypeOptions[0],
  createdDate: moment().format('YYYY-MM-DD'),
}

const PaymentAccountsPage = () => {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [formState, setFormState] = useState(initialForm)
  const [mode, setMode] = useState('create')
  const [activeId, setActiveId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const bankLookup = bankTypeLookup

  useEffect(() => {
    let isMounted = true

    const loadAccounts = async () => {
      try {
        setLoading(true)
        const response = await bankAccountService.list()
        const payload = response?.data ?? []
        const formatted =
          payload?.map((account) => {
            const bankTypeValue =
              account.bankType ??
              account.bankAccountType ??
              account.bankTypeId ??
              account.bank_code
            const normalizedType = normalizeBankTypeValue(bankTypeValue)
            const createdMoment = moment(account.createdDate ?? account.createdAt)

            return {
              id: account.bankAccountId ?? account.id,
              accountName: account.bankAccountName ?? account.accountName,
              accountNumber: account.bankAccountNumber ?? account.accountNumber,
              bankType: normalizedType,
              createdDate: createdMoment.format('DD MMM YYYY'),
              rawCreatedDate: createdMoment.format('YYYY-MM-DD'),
            }
          }) ?? []

        if (isMounted) {
          console.log('Loaded accounts:', formatted)
          setAccounts(formatted)
        }
      } catch (error) {
        if (isMounted) {
          toast.error('Unable to load payment accounts. Showing fallback data.')
          setAccounts([
            {
              id: 'seed-1',
              accountName: 'KBZ Collections',
              accountNumber: '9988776655',
              bankType: 'KBZ_BANK',
              createdDate: moment().subtract(45, 'days').format('DD MMM YYYY'),
              rawCreatedDate: moment().subtract(45, 'days').format('YYYY-MM-DD'),
            },
            {
              id: 'seed-2',
              accountName: 'AYA Withdrawal Ops',
              accountNumber: '2233116677',
              bankType: 'AYA_BANK',
              createdDate: moment().subtract(12, 'days').format('DD MMM YYYY'),
              rawCreatedDate: moment().subtract(12, 'days').format('YYYY-MM-DD'),
            },
          ])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadAccounts()

    return () => {
      isMounted = false
    }
  }, [])

  const resetForm = () => {
    setMode('create')
    setActiveId(null)
    setFormState({
      ...initialForm,
      createdDate: moment().format('YYYY-MM-DD'),
    })
  }

  const handleChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleCreate = async () => {
    try {
      setSaving(true)
      const payload = {
        bankAccountName: formState.accountName,
        bankAccountNumber: formState.accountNumber,
        bankType: normalizeBankTypeValue(formState.bankType),
        createdDate: formState.createdDate,
      }
      const response = await bankAccountService.create(payload)
      const created = response?.data ?? payload
      const record = {
        id: created.bankAccountId ?? created.id ?? Date.now(),
        accountName: created.bankAccountName ?? payload.bankAccountName,
        accountNumber: created.bankAccountNumber ?? payload.bankAccountNumber,
        bankType: normalizeBankTypeValue(created.bankType ?? payload.bankType),
        createdDate: moment(created.createdDate ?? payload.createdDate).format(
          'DD MMM YYYY',
        ),
        rawCreatedDate: moment(created.createdDate ?? payload.createdDate).format(
          'YYYY-MM-DD',
        ),
      }
      setAccounts((prev) => [record, ...prev])
      toast.success('Account created successfully')
      resetForm()
    } catch (error) {
      toast.error('Unable to create account right now.')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async () => {
    try {
      setSaving(true)
      const payload = {
        bankAccountId: activeId,
        bankAccountName: formState.accountName,
        bankAccountNumber: formState.accountNumber,
        bankType: normalizeBankTypeValue(formState.bankType),
        createdDate: formState.createdDate,
      }
      await bankAccountService.update(payload)
      setAccounts((prev) =>
        prev.map((account) =>
          account.id === activeId
            ? {
                ...account,
                accountName: payload.bankAccountName,
                accountNumber: payload.bankAccountNumber,
                bankType: normalizeBankTypeValue(payload.bankType),
                createdDate: moment(payload.createdDate).format('DD MMM YYYY, hh:mm A'),
                rawCreatedDate: payload.createdDate,
              }
            : account,
        ),
      )
      toast.success('Account updated successfully')
      resetForm()
    } catch (error) {
      toast.error('Failed to update the account.')
    } finally {
      setSaving(false)
    }
  }

  const handleSubmit = () => {
    if (mode === 'edit') {
      handleUpdate()
    } else {
      handleCreate()
    }
  }

  const handleEdit = (account) => {
    //console.log('Editing account', account)
    setMode('edit')
    setActiveId(account.id)
    setFormState({
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      bankType: bankLookup.get(account.bankType) ?? account.bankType,
      createdDate: account.rawCreatedDate ?? moment(account.createdDate, 'DD MMM YYYY').format('YYYY-MM-DD'),
    })
  }

  const openDeleteDialog = (account) => {
    setPendingDelete(account)
    setDeleteDialogOpen(true)
  }

  const closeDeleteDialog = (force = false) => {
    if (deleting && !force) return
    setDeleteDialogOpen(false)
    setPendingDelete(null)
  }

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return

    try {
      setDeleting(true)
      await bankAccountService.remove(pendingDelete.id)
      setAccounts((prev) => prev.filter((item) => item.id !== pendingDelete.id))
      toast.success('Account removed')
      if (pendingDelete.id === activeId) {
        resetForm()
      }
      closeDeleteDialog(true)
    } catch (error) {
      toast.error('Unable to delete this account right now.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(280px,360px)_1fr]">
      <div className="order-2 lg:order-1 lg:sticky lg:top-6">
        <AccountCreatePanel
          mode={mode}
          formState={formState}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onReset={resetForm}
          loading={saving}
          bankOptions={bankTypeOptions}
        />
      </div>
      <div className="order-1 lg:order-2 space-y-6">
        <div className="block lg:hidden">
          {loading ? (
            <div className="min-h-[220px] rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-500 dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60 dark:text-customMuted">
              <div className="flex flex-col items-center gap-2">
                <MdDataSaverOff className="h-6 w-6 animate-spin-fast" />
                Loading accounts...
              </div>
            </div>
          ) : (
            <MobileAccountList
              accounts={accounts}
              onEdit={handleEdit}
              onDelete={openDeleteDialog}
              bankLookup={bankLookup}
            />
          )}
        </div>
        <div className="hidden min-h-[320px] lg:block">
          {loading ? (
            <div className="flex h-full min-h-[320px] items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500 dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60 dark:text-customMuted">
              <MdDataSaverOff className="h-6 w-6 animate-spin-fast" /> Loading accounts...
            </div>
          ) : (
            <AccountTable
              accounts={accounts}
              onEdit={handleEdit}
              onDelete={openDeleteDialog}
              bankLookup={bankLookup}
            />
          )}
        </div>
      </div>
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            closeDeleteDialog()
          } else {
            setDeleteDialogOpen(true)
          }
        }}
        title="Delete payment account"
        contextText={`Are you sure you want to delete ${pendingDelete?.accountName ?? 'this account'}? This action cannot be undone.`}
        confirmLabel={deleting ? 'Removing...' : 'Delete account'}
        cancelLabel="Cancel"
        appearance="danger"
        isProcessing={deleting}
        handleSubmit={handleDeleteConfirmed}
      />
    </div>
  )
}

export default PaymentAccountsPage
